import { L10nBase, ModelBase, ObservableCollection } from '@skylab-shared/core';
import { DocumentPrintingPatternModel, InventoryDocumentKind, PrintingPatternGridColumnDefinition, PrintingPatternModel, SaleDocumentKind } from '@skylab-shared/dataModel';
import { RecursiveDiff } from '@skylab-shared/types';

export interface PrintingPatternTypesConfig {
    Invoice: PrintingPatternTypeConfig;
    AdvanceInvoice: PrintingPatternTypeConfig;
    Receipt: PrintingPatternTypeConfig;
    ProForma: PrintingPatternTypeConfig;
    VatMarginInvoice: PrintingPatternTypeConfig;
    InvoiceAdjustment: PrintingPatternTypeConfig;
    AdvanceInvoiceAdjustment: PrintingPatternTypeConfig;
    ReceiptAdjustment: PrintingPatternTypeConfig;
    VatMarginInvoiceAdjustment: PrintingPatternTypeConfig;
    GoodsIssue: PrintingPatternTypeConfig;
    InternalGoodsIssue: PrintingPatternTypeConfig;
    GoodsIssueAdjustment: PrintingPatternTypeConfig;
    GoodsReceipt: PrintingPatternTypeConfig;
    InternalGoodsReceipt: PrintingPatternTypeConfig;
    GoodsReceiptAdjustment: PrintingPatternTypeConfig;
}

export interface PrintingPatternTypeConfig {
    value: SaleDocumentKind | InventoryDocumentKind;
    label: string;
    columns: any;
    getDefaults(): RecursiveDiff<PrintingPatternModel, ModelBase>;
}

export class PrintingPatternConfigBase {
    private config: PrintingPatternTypesConfig;
    public printingPatternTypes: { value: SaleDocumentKind | InventoryDocumentKind; label: string }[];
    private allColumns;

    public constructor(
        private readonly l10n: L10nBase
    ) {
        this.printingPatternTypes = [
            { value: SaleDocumentKind.Invoice, label: this.l10n._T('business.printing-pattern.types.invoice.label') },
            { value: SaleDocumentKind.Receipt, label: this.l10n._T('business.printing-pattern.types.receipt.label') },
            { value: SaleDocumentKind.AdvanceInvoice, label: this.l10n._T('business.printing-pattern.types.advance-invoice.label')},
            { value: SaleDocumentKind.VatMarginInvoice, label: this.l10n._T('business.printing-pattern.types.vat-margin-invoice.label')},
            { value: SaleDocumentKind.ProForma, label: this.l10n._T('business.printing-pattern.types.pro-forma-invoice.label') },
            { value: SaleDocumentKind.InvoiceAdjustment, label: this.l10n._T('business.printing-pattern.types.invoice-adjustment.label') },
            { value: SaleDocumentKind.AdvanceInvoiceAdjustment, label: this.l10n._T('business.printing-pattern.types.advance-invoice-adjustment.label') },
            { value: SaleDocumentKind.ReceiptAdjustment, label: this.l10n._T('business.printing-pattern.types.receipt-adjustment.label') },
            { value: SaleDocumentKind.VatMarginInvoiceAdjustment, label: this.l10n._T('business.printing-pattern.types.vat-margin-invoice-adjustment.label') },
            { value: InventoryDocumentKind.GoodsIssue, label: this.l10n._T('business.printing-pattern.types.goods-issue.label') },
            { value: InventoryDocumentKind.InternalGoodsIssue, label: this.l10n._T('business.printing-pattern.types.internal-goods-issue.label') },
            { value: InventoryDocumentKind.GoodsIssueAdjustment, label: this.l10n._T('business.printing-pattern.types.goods-issue-adjustment.label') },
            { value: InventoryDocumentKind.GoodsReceipt, label: this.l10n._T('business.printing-pattern.types.goods-receipt.label')},
            { value: InventoryDocumentKind.InternalGoodsReceipt, label: this.l10n._T('business.printing-pattern.types.internal-goods-receipt.label')},
            { value: InventoryDocumentKind.GoodsReceiptAdjustment, label: this.l10n._T('business.printing-pattern.types.goods-receipt-adjustment.label')}
        ];

        this.allColumns = {
            Item_Discount: { id: 'Item_Discount', name: this.l10n.instant('business.printing-pattern.columns.discount'), width: 1.5 },
            Item_No: { id: 'Item_No', name: this.l10n.instant('business.printing-pattern.columns.ordinal-number'), width: 0.8 },
            Item_PKWiU: { id: 'Item_PKWiU', name: this.l10n.instant('business.printing-pattern.columns.pkwiu'), width: 2.0 },
            Item_CnCode: { id: 'Item_CNCode', name: this.l10n.instant('business.printing-pattern.columns.combined-nomenclature'), width: 2.0 },
            Item_BarCode: { id: 'Item_BarCode', name: this.l10n.instant('business.printing-pattern.columns.bar-code'), width: 2.0 },
            Item_ProductName: { id: 'Item_ProductName', name: this.l10n.instant('business.printing-pattern.columns.product-name'), width: 5.5 },
            Item_Symbol: { id: 'Item_Symbol', name: this.l10n.instant('business.printing-pattern.columns.symbol'), width: 2.0 },
            Item_Quantity: { id: 'Item_Quantity', name: this.l10n.instant('business.printing-pattern.columns.quantity'), width: 1.8 },
            Item_VatRate: { id: 'Item_VatRate', name: this.l10n.instant('business.printing-pattern.columns.vat-rate'), availableForVatPayer: true, width: 1.1 },
            Item_JPKCodes: { id: 'Item_JPKCodes', name: this.l10n.instant('business.printing-pattern.columns.jpk-codes'), availableForVatPayer: true, width: 2.0 }, //GTU
            Item_ItemDescription: { id: 'Item_ItemDescription', name: this.l10n.instant('business.printing-pattern.columns.item-description'), availableForVatPayer: true, width: 2.0 },
            // póki co kolumna ukryta, https://skylab.myjetbrains.com/youtrack/issue/SL-7602
            //Item_JPKProcedure: { id: 'Item_JPKProcedure', name: this.l10n.instant('business.printing-pattern.columns.jpk-procedure'), availableForVatPayer: true, width: 2.0 },

            //ceny
            Item_UnitGrossPrice: { id: 'Item_UnitGrossPriceAfterDiscount', name: this.l10n.instant('business.printing-pattern.columns.unit-gross-price'), width: 2.0, availableForVatPayer: true },
            Item_UnitGrossPriceBeforeDiscount: { id: 'Item_UnitGrossPrice', name: this.l10n.instant('business.printing-pattern.columns.unit-gross-price-before-discount'), width: 2.0, availableForVatPayer: true },
            Item_UnitNetPrice: { id: 'Item_UnitNetPriceAfterDiscount', name: this.l10n.instant('business.printing-pattern.columns.unit-net-price'), width: 2.1, availableForVatPayer: true },
            Item_UnitNetPriceBeforeDiscount: { id: 'Item_UnitNetPrice', name: this.l10n.instant('business.printing-pattern.columns.unit-net-price-before-discount'), width: 2.0, availableForVatPayer: true },
            Item_UnitPrice: { id: 'Item_UnitPriceAfterDiscount', name: this.l10n.instant('business.printing-pattern.columns.unit-price'), width: 2.0, availableForVatPayer: false },
            Item_UnitPriceBeforeDiscount: { id: 'Item_UnitPrice', name: this.l10n.instant('business.printing-pattern.columns.unit-price-before-discount'), width: 2.0, availableForVatPayer: false },

            //wartości
            Item_GrossValue: { id: 'Item_GrossValueAfterDiscount', name: this.l10n.instant('business.printing-pattern.columns.gross-value'), width: 2.1, availableForVatPayer: true },
            Item_GrossValueBeforeDiscount: { id: 'Item_GrossValue', name: this.l10n.instant('business.printing-pattern.columns.gross-value-before-discount'), width: 2.0, availableForVatPayer: true },
            Item_NetValue: { id: 'Item_NetValueAfterDiscount', name: this.l10n.instant('business.printing-pattern.columns.net-value'), width: 2.0, availableForVatPayer: true },
            Item_NetValueBeforeDiscount: { id: 'Item_NetValue', name: this.l10n.instant('business.printing-pattern.columns.net-value-before-discount'), width: 2.0, availableForVatPayer: true },
            Item_Value: { id: 'Item_ValueAfterDiscount', name: this.l10n.instant('business.printing-pattern.columns.value'), width: 2.0, availableForVatPayer: false },
            Item_ValueBeforeDiscount: { id: 'Item_Value', name: this.l10n.instant('business.printing-pattern.columns.value-before-discount'), width: 2.0, availableForVatPayer: false },
            Item_VatValue: { id: 'Item_VatValue', name: this.l10n.instant('business.printing-pattern.columns.vat-value'), width: 2.1, availableForVatPayer: true },
            Item_Cost: { id: 'Item_Cost', name: this.l10n.instant('business.printing-pattern.columns.cost'), width: 2.1 }
        };

        this.config = {
            Invoice: {
                value: SaleDocumentKind.Invoice,
                label: this.l10n.instant('business.printing-pattern.types.invoice.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription
                ],
                getDefaults: () => this.getInvoiceDefaults()
            },
            AdvanceInvoice: {
                value: SaleDocumentKind.AdvanceInvoice,
                label: this.l10n.instant('business.printing-pattern.types.advance-invoice.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription
                ],
                getDefaults: () => this.getAdvanceInvoiceDefaults()
            },
            Receipt: {
                value: SaleDocumentKind.Receipt,
                label: this.l10n.instant('business.printing-pattern.types.receipt.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription
                ],
                getDefaults: () => this.getReceiptDefaults()
            },
            ProForma: {
                value: SaleDocumentKind.ProForma,
                label: this.l10n.instant('business.printing-pattern.types.pro-forma-invoice.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription
                ],
                getDefaults: () => this.getProFormaDefaults()
            },
            VatMarginInvoice: {
                value: SaleDocumentKind.VatMarginInvoice,
                label: this.l10n.instant('business.printing-pattern.types.vat-margin-invoice.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription
                ],
                getDefaults: () => this.getVatMarginInvoiceDefaults()
            },
            InvoiceAdjustment: {
                value: SaleDocumentKind.InvoiceAdjustment,
                label: this.l10n.instant('business.printing-pattern.types.invoice-adjustment.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription
                ],
                getDefaults: () => this.getInvoiceAdjustmentDefaults()
            },
            AdvanceInvoiceAdjustment: {
                value: SaleDocumentKind.AdvanceInvoiceAdjustment,
                label: this.l10n.instant('business.printing-pattern.types.advance-invoice-adjustment.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription
                ],
                getDefaults: () => this.getAdvanceInvoiceAdjustmentDefaults()
            },
            ReceiptAdjustment: {
                value: SaleDocumentKind.ReceiptAdjustment,
                label: this.l10n.instant('business.printing-pattern.types.receipt-adjustment.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription
                ],
                getDefaults: () => this.getReceiptAdjustmentDefaults()
            },
            VatMarginInvoiceAdjustment: {
                value: SaleDocumentKind.VatMarginInvoiceAdjustment,
                label: this.l10n.instant('business.printing-pattern.types.vat-margin-invoice-adjustment.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription
                ],
                getDefaults: () => this.getVatMarginInvoiceAdjustmentDefaults()
            },
            GoodsIssue: {
                value: InventoryDocumentKind.GoodsIssue,
                label: this.l10n.instant('business.printing-pattern.types.goods-issue.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription,
                    this.allColumns.Item_Cost
                ],
                getDefaults: () => this.getGoodsIssueDefaults()
            },
            InternalGoodsIssue: {
                value: InventoryDocumentKind.InternalGoodsIssue,
                label: this.l10n.instant('business.printing-pattern.types.internal-goods-issue.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription,
                    this.allColumns.Item_Cost
                ],
                getDefaults: () => this.getInternalGoodsIssueDefaults()
            },
            GoodsIssueAdjustment: {
                value: InventoryDocumentKind.GoodsIssueAdjustment,
                label: this.l10n.instant('business.printing-pattern.types.goods-issue-adjustment.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription,
                    this.allColumns.Item_Cost
                ],
                getDefaults: () => this.getGoodsIssueAdjustmentDefaults()
            },
            GoodsReceipt: {
                value: InventoryDocumentKind.GoodsReceipt,
                label: this.l10n.instant('business.printing-pattern.types.goods-receipt.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription,
                    this.allColumns.Item_Cost
                ],
                getDefaults: () => this.getGoodsReceiptDefaults()
            },
            InternalGoodsReceipt: {
                value: InventoryDocumentKind.InternalGoodsReceipt,
                label: this.l10n.instant('business.printing-pattern.types.internal-goods-receipt.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription,
                    this.allColumns.Item_Cost
                ],
                getDefaults: () => this.getInternalGoodsReceiptDefaults()
            },
            GoodsReceiptAdjustment: {
                value: InventoryDocumentKind.GoodsReceiptAdjustment,
                label: this.l10n.instant('business.printing-pattern.types.goods-receipt-adjustment.label'),
                columns: [
                    this.allColumns.Item_No,
                    this.allColumns.Item_ProductName,
                    this.allColumns.Item_Symbol,
                    this.allColumns.Item_PKWiU,
                    this.allColumns.Item_CnCode,
                    this.allColumns.Item_BarCode,
                    this.allColumns.Item_Quantity,
                    this.allColumns.Item_Discount,
                    this.allColumns.Item_VatRate,
                    this.allColumns.Item_UnitNetPriceBeforeDiscount,
                    this.allColumns.Item_UnitNetPrice,
                    this.allColumns.Item_UnitGrossPriceBeforeDiscount,
                    this.allColumns.Item_UnitGrossPrice,
                    this.allColumns.Item_NetValueBeforeDiscount,
                    this.allColumns.Item_NetValue,
                    this.allColumns.Item_GrossValueBeforeDiscount,
                    this.allColumns.Item_GrossValue,
                    this.allColumns.Item_VatValue,
                    this.allColumns.Item_UnitPriceBeforeDiscount,
                    this.allColumns.Item_UnitPrice,
                    this.allColumns.Item_ValueBeforeDiscount,
                    this.allColumns.Item_Value,
                    this.allColumns.Item_JPKCodes,
                    this.allColumns.Item_ItemDescription,
                    this.allColumns.Item_Cost
                ],
                getDefaults: () => this.getGoodsReceiptAdjustmentDefaults()
            }
        };
    }

    private getInvoiceDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.invoice.default-document-name'),
        kind: SaleDocumentKind.Invoice,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            baseCurrencyDocumentGross: false,
            baseCurrencyVatTable: false,
            deliveryAddress: true,
            documentNote: false,
            exchangeRate: false,
            issuer: true,
            itemNote: false,
            leftToPay: false,
            logo: true,
            productNote: false,
            recipient: true,
            relatedClientTIN: false,
            sourceDocumentInfo: false,
            vatTableWhenReverseCharge: true,
            explainSaleDate: false,
            jpkCodes: false,
            unsettledDocuments: false,
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getAdvanceInvoiceDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.advance-invoice.default-document-name'),
        kind: SaleDocumentKind.AdvanceInvoice,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            baseCurrencyDocumentGross: false,
            baseCurrencyVatTable: false,
            deliveryAddress: true,
            documentNote: false,
            exchangeRate: false,
            issuer: true,
            itemNote: false,
            leftToPay: false,
            logo: true,
            productNote: false,
            recipient: true,
            relatedClientTIN: false,
            sourceDocumentInfo: false,
            vatTableWhenReverseCharge: true,
            explainSaleDate: false,
            jpkCodes: false,
            unsettledDocuments: false
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getProFormaDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.pro-forma-invoice.default-document-name'),
        kind: SaleDocumentKind.ProForma,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            baseCurrencyDocumentGross: false,
            baseCurrencyVatTable: false,
            deliveryAddress: true,
            documentNote: false,
            exchangeRate: false,
            issuer: true,
            itemNote: false,
            leftToPay: false,
            logo: true,
            productNote: false,
            recipient: true,
            relatedClientTIN: false,
            sourceDocumentInfo: false,
            vatTableWhenReverseCharge: false,
            jpkCodes: false,
            unsettledDocuments: false
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getReceiptDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.receipt.default-document-name'),
        kind: SaleDocumentKind.Receipt,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            baseCurrencyDocumentGross: false,
            baseCurrencyVatTable: false,
            deliveryAddress: true,
            documentNote: false,
            exchangeRate: false,
            issuer: true,
            itemNote: false,
            leftToPay: false,
            logo: true,
            productNote: false,
            recipient: true,
            relatedClientTIN: false,
            sourceDocumentInfo: false,
            vatTableWhenReverseCharge: false,
            explainSaleDate: false,
            jpkCodes: false,
            unsettledDocuments: false
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getVatMarginInvoiceDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.vat-margin-invoice.default-document-name'),
        kind: SaleDocumentKind.VatMarginInvoice,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            baseCurrencyDocumentGross: false,
            baseCurrencyVatTable: false,
            deliveryAddress: true,
            documentNote: false,
            exchangeRate: false,
            issuer: true,
            itemNote: false,
            leftToPay: false,
            logo: true,
            productNote: false,
            recipient: true,
            relatedClientTIN: false,
            sourceDocumentInfo: false,
            vatTableWhenReverseCharge: false,
            explainSaleDate: false,
            jpkCodes: false,
            unsettledDocuments: false
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitGrossPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPriceBeforeDiscount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ValueBeforeDiscount)
            ])
        }
    });

    private getInvoiceAdjustmentDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.invoice-adjustment.default-document-name'),
        kind: SaleDocumentKind.InvoiceAdjustment,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            baseCurrencyDocumentGross: false,
            baseCurrencyVatTable: false,
            deliveryAddress: true,
            documentNote: false,
            exchangeRate: false,
            issuer: true,
            itemNote: false,
            leftToPay: false,
            logo: true,
            productNote: false,
            recipient: true,
            relatedClientTIN: false,
            sourceDocumentInfo: false,
            vatTableWhenReverseCharge: true,
            explainSaleDate: false,
            jpkCodes: false,
            unsettledDocuments: false
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getAdvanceInvoiceAdjustmentDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.advance-invoice-adjustment.default-document-name'),
        kind: SaleDocumentKind.AdvanceInvoiceAdjustment,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            baseCurrencyDocumentGross: false,
            baseCurrencyVatTable: false,
            deliveryAddress: true,
            documentNote: false,
            exchangeRate: false,
            issuer: true,
            itemNote: false,
            leftToPay: false,
            logo: true,
            productNote: false,
            recipient: true,
            relatedClientTIN: false,
            sourceDocumentInfo: false,
            vatTableWhenReverseCharge: true,
            explainSaleDate: false,
            jpkCodes: false,
            unsettledDocuments: false
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getReceiptAdjustmentDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.receipt-adjustment.default-document-name'),
        kind: SaleDocumentKind.ReceiptAdjustment,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            baseCurrencyDocumentGross: false,
            baseCurrencyVatTable: false,
            deliveryAddress: true,
            documentNote: false,
            exchangeRate: false,
            issuer: true,
            itemNote: false,
            leftToPay: false,
            logo: true,
            productNote: false,
            recipient: true,
            relatedClientTIN: false,
            sourceDocumentInfo: false,
            vatTableWhenReverseCharge: false,
            explainSaleDate: false,
            jpkCodes: false,
            unsettledDocuments: false
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getVatMarginInvoiceAdjustmentDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.vat-margin-invoice-adjustment.default-document-name'),
        kind: SaleDocumentKind.VatMarginInvoiceAdjustment,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            baseCurrencyDocumentGross: false,
            baseCurrencyVatTable: false,
            deliveryAddress: true,
            documentNote: false,
            exchangeRate: false,
            issuer: true,
            itemNote: false,
            leftToPay: false,
            logo: true,
            productNote: false,
            recipient: true,
            relatedClientTIN: false,
            sourceDocumentInfo: false,
            vatTableWhenReverseCharge: false,
            explainSaleDate: false,
            jpkCodes: false,
            unsettledDocuments: false
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitGrossPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPriceBeforeDiscount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ValueBeforeDiscount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value),
            ])
        }
    });

    private getGoodsIssueDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.goods-issue.default-document-name'),
        kind: InventoryDocumentKind.GoodsIssue,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            logo: true,
            documentNote: false,
            itemNote: false,
            productNote: false,
            relatedClientTIN: false,
            deliveryAddress: true,
            exchangeRate: false,
            baseCurrencyDocumentGross: false,
            vatTable: false,
            baseCurrencyVatTable: false,
            issuer: true,
            recipient: true,
            jpkCodes: false,
            summary: true
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getInternalGoodsIssueDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.internal-goods-issue.default-document-name'),
        kind: InventoryDocumentKind.InternalGoodsIssue,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            logo: true,
            documentNote: false,
            itemNote: false,
            productNote: false,
            relatedClientTIN: false,
            exchangeRate: false,
            baseCurrencyDocumentGross: false,
            vatTable: false,
            baseCurrencyVatTable: false,
            issuer: true,
            recipient: true,
            jpkCodes: false,
            summary: true
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getGoodsIssueAdjustmentDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.goods-issue-adjustment.default-document-name'),
        kind: InventoryDocumentKind.GoodsIssueAdjustment,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            logo: true,
            documentNote: false,
            itemNote: false,
            productNote: false,
            relatedClientTIN: false,
            deliveryAddress: true,
            exchangeRate: false,
            baseCurrencyDocumentGross: false,
            vatTable: false,
            baseCurrencyVatTable: false,
            issuer: true,
            recipient: true,
            jpkCodes: false,
            summary: true
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getGoodsReceiptDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.goods-receipt.default-document-name'),
        kind: InventoryDocumentKind.GoodsReceipt,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            logo: true,
            documentNote: false,
            itemNote: false,
            productNote: false,
            relatedClientTIN: false,
            deliveryAddress: true,
            exchangeRate: false,
            baseCurrencyDocumentGross: false,
            vatTable: false,
            baseCurrencyVatTable: false,
            issuer: true,
            recipient: true,
            jpkCodes: false,
            summary: true
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getInternalGoodsReceiptDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.internal-goods-receipt.default-document-name'),
        kind: InventoryDocumentKind.InternalGoodsReceipt,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            logo: true,
            documentNote: false,
            itemNote: false,
            productNote: false,
            relatedClientTIN: false,
            deliveryAddress: true,
            exchangeRate: false,
            baseCurrencyDocumentGross: false,
            vatTable: false,
            baseCurrencyVatTable: false,
            issuer: true,
            recipient: true,
            jpkCodes: false,
            summary: true
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    private getGoodsReceiptAdjustmentDefaults = (): RecursiveDiff<PrintingPatternModel, ModelBase> => ({
        default: true,
        id: 0,
        name: this.l10n.instant('business.printing-pattern.types.goods-receipt-adjustment.default-document-name'),
        kind: InventoryDocumentKind.GoodsReceiptAdjustment,
        disableShadows: false,
        note: '',
        documentTitle: '',
        myCompanyNote: '',
        secondaryLanguage: null,
        includes: new DocumentPrintingPatternModel({
            logo: true,
            documentNote: false,
            itemNote: false,
            productNote: false,
            relatedClientTIN: false,
            deliveryAddress: true,
            exchangeRate: false,
            baseCurrencyDocumentGross: false,
            vatTable: false,
            baseCurrencyVatTable: false,
            issuer: true,
            recipient: true,
            jpkCodes: false,
            summary: true
        }),
        itemsList: {
            columns: new ObservableCollection([
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_No),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_ProductName),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Quantity),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Discount),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitNetPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatRate),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_NetValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_VatValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_GrossValue),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_UnitPrice),
                new PrintingPatternGridColumnDefinition(this.allColumns.Item_Value)
            ])
        }
    });

    public getPrintingPatternConfig(type: keyof PrintingPatternTypesConfig, vatPayer?: boolean): PrintingPatternTypeConfig {
        const config: PrintingPatternTypeConfig = { ...this.config[type] };
        config.columns = config.columns.filter((c: any) => c.availableForVatPayer === void 0 || c.availableForVatPayer === vatPayer);
        return config;
    }
}
